<template>
    <div
        class="opportunity-details-widget"
        :class="{
            'editing-opportunity': isEditingOpportunity,
            'navigation-disabled': !allowNavigation
        }"
    >
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ headerLabel }}</h3>
                <div class="svg-container widget-close-button" @click="handleClickCloseWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="loading" />
                <template v-else>
                    <tab-navigation
                        v-if="allowNavigation"
                        :value="selectedNavItem"
                        :options="navItems"
                        keep-tab-value
                        @input="handleChangeNavItem"
                    />
                    <div class="opportunity-details-tabs">
                        <opportunity-details-tab v-if="selectedNavItemIndex === 0" />
                        <opportunity-activities-tab v-if="selectedNavItemIndex === 1" />
                        <opportunity-registered-deals-tab v-if="selectedNavItemIndex === 2" />
                        <opportunity-files-tab v-if="selectedNavItemIndex === 3" />
                        <opportunity-summary-tab
                            v-if="!isNewOpportunity && selectedNavItemIndex === 4" />
                    </div>
                </template>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="handleClickCloseWidget">
                        {{ isEditingOpportunity ? 'Cancel' : 'Close' }}
                    </app-button>
                </div>
                <div class="action-container">
                    <div class="activity-buttons">
                        <template v-if="showNavigationButtons">
                            <app-button
                                v-if="selectedNavItemIndex > 0"
                                class="btn-secondary"
                                @click="navigatePreviousTab"
                            >
                                Back
                            </app-button>
                            <app-button
                                v-if="selectedNavItemIndex < navItems.length - 1"
                                class="btn-secondary" @click="navigateNextTab"
                            >
                                Next: {{ navItems[selectedNavItemIndex + 1].label }}
                            </app-button>
                        </template>
                        <template v-if="isEditingOpportunity">
                            <app-button :loading="isLoading || isSaving" @click="saveOpportunity">
                                {{ saveButtonLabel }}
                            </app-button>
                        </template>
                        <template v-else>
                            <app-button :loading="isLoading || isSaving" @click="editOpportunity">
                                {{ isLoading || isSaving ? 'Loading' : 'Edit Details' }}
                            </app-button>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { createOpportunityDetailsWidgetStore } from './opportunity-details-widget-store';
import EventBus from '@/scripts/event-bus';
import { CustomFieldsServiceMixin } from '@/mixins/custom-fields-service-mixin';
import { OpportunityWidgetMixin } from './opportunity-widget-mixin';
import { cloneDeep, differenceWith, isEqual, isNumber } from 'lodash';
import { required } from 'vuelidate/lib/validators';
import AppPopup from '@/components/popups/app-popup.vue';
import { OPPORTUNITY_EDIT_CONTEXT } from './opportunity-helpers';

const TabNavigation = () => import('@/components/tab-navigation');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const OpportunityDetailsTab = () => import('./opportunity-details-tab/opportunity-details-tab.vue');
const OpportunityActivitiesTab = () => import('./opportunity-activities-tab/opportunity-activities-tab.vue');
const OpportunityRegisteredDealsTab = () => import('./opportunity-registered-deals-tab/opportunity-registered-deals-tab.vue');
const OpportunityFilesTab = () => import('./opportunity-files-tab/opportunity-files-tab.vue');
const OpportunitySummaryTab = () => import('./opportunity-summary-tab/opportunity-summary-tab.vue');
const AppButton = () => import('@/components/common/app-button');

export default {
    name: 'opportunity-details-widget',

    components: {
        'opportunity-details-tab': OpportunityDetailsTab,
        'opportunity-activities-tab': OpportunityActivitiesTab,
        'opportunity-registered-deals-tab': OpportunityRegisteredDealsTab,
        'opportunity-files-tab': OpportunityFilesTab,
        'opportunity-summary-tab': OpportunitySummaryTab,
        'tab-navigation': TabNavigation,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-button': AppButton
    },

    mixins: [CustomFieldsServiceMixin, OpportunityWidgetMixin],

    provide() {
        return {
            contextStore: this.contextStore,
            $v: this.$v
        };
    },

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    validations: {
        opportunity: {
            name: { required },
            status: {
                required,
                id: { required },
                notWon(value) {
                    if (value && !this.hasMetMinimumGrossMargin) {
                        return value.isWon !== true;
                    }
                    return true;
                }
            },

            salespersons: { required },
            type: {
                id: { required }
            },
            contact: { required },
            company: { required },
            description: { required }
        }
    },

    data() {
        return {
            contextStore: createOpportunityDetailsWidgetStore(),
            customOpportunityFields: [],
            initialCustomOpportunityFields: [],
        };
    },

    computed: {
        headerLabel() {
            if (this.isNewOpportunity) {
                return 'Add Opportunity';
            } else if (this.isEditingOpportunity) {
                if (this.editingContext === OPPORTUNITY_EDIT_CONTEXT.Details) {
                    return 'Edit Opportunity Details';
                } else {
                    return 'Edit Opportunity';
                }
            }

            return 'Opportunity Details';
        },

        saveButtonLabel() {
            if (this.isEditingOpportunity && this.editingContext === OPPORTUNITY_EDIT_CONTEXT.Details) {
                return 'Save Changes';
            } else {
                return 'Save Opportunity';
            }
        },

        loading() {
            return this.contextStore.getters.loading;
        },

        allowNavigation() {
            return (this.isEditingOpportunity && this.editingContext === OPPORTUNITY_EDIT_CONTEXT.All) || (!this.isEditingOpportunity && !this.isNewOpportunity);
        },

        showNavigationButtons() {
            return this.allowNavigation && !this.isLoading && this.editingContext === OPPORTUNITY_EDIT_CONTEXT.All;
        },

        navItems() {
            return this.contextStore.getters.navItems;
        },

        selectedNavItem() {
            return this.contextStore.getters.selectedNavItem;
        },

        selectedNavItemIndex() {
            return this.contextStore.state.selectedNavItemIndex;
        },

        customFieldsConfig() {
            return {
                type: 'Opportunity',
                clientId: this.clientId,
                opportunityId: this.opportunity.id
            };
        },

        discountOptions() {
            return this.contextStore.state.detailsTab.discountOptions;
        },

        hasMetMinimumGrossMargin() {
            return this.contextStore.getters.hasMetMinimumGrossMargin;
        },

        secondaryContactsQuery() {
            return this.contextStore.state.secondaryContactsQuery;
        },

        isPricingDetailsSimpleEntry() {
            return this.opportunity.pricingDetails === 'simple';
        }
    },

    watch: {
        isEditingOpportunity() {
            if (!this.isSaving)
            {
                this.loadCustomOpportunityFields();
            }
        },

        'opportunity.contact': {
            immediate: false,
            deep: true,
            handler(contact) {
                if (!this.isEditingOpportunity) {
                    return;
                }

                if (contact) {
                    const { companyId } = contact;
                    if (companyId != null) {
                        this.contextStore.dispatch('setCompanyBySelectedContact', companyId);
                    } else {
                        this.updateOpportunity((o) => {
                            o.company = null;
                        });
                    }
                }
            }
        },

        'opportunity.secondaryContacts'() {
            if (!this.isEditingOpportunity) {
                return;
            }

            if (this.opportunity.secondaryContacts[0]) {
                const { companyId } = this.opportunity.secondaryContacts[0];
                if (companyId != null) {
                    this.contextStore.dispatch('setCompanyBySelectedContact', companyId);
                } else {
                    this.updateOpportunity((o) => {
                        o.company = null;
                    });
                }
            }
        },

        'opportunity.company'(newValue, oldValue) {
            if (!this.isEditingOpportunity) {
                return;
            }

            if (this.secondaryContactsQuery) {
                const { query, limit } = this.secondaryContactsQuery;
                this.contextStore.dispatch('searchContacts', { query, limit });
            }

            if (
                (oldValue && newValue.id !== oldValue.id) ||
                (this.opportunity.secondaryContacts && this.opportunity.secondaryContacts.length &&
                    this.opportunity.secondaryContacts[0].companyId !== newValue.id)
            ) {
                this.updateOpportunity((o) => {
                    o.secondaryContacts = [];
                });
            }
        }
    },


    created() {
        EventBus.$on('opportunity:update', this.handleOpportunityUpdate);
        EventBus.$on('opportunity-widget:validate-and-save', this.handleValidateAndSave);
        EventBus.$on('custom-field:update', this.loadCustomOpportunityFields);

        this.contextStore.commit('initialize', {
            opportunityId: this.widget.opportunityId,
            editing: this.widget.editing,
            editingContext: this.widget.editingContext,
            selectedNavItemIndex: this.widget.selectedNavItemIndex,
            widget: this.widget
        });

        this.contextStore.dispatch('loadMinimumGrossMargin');

        if (this.isEditingOpportunity) {
            this.contextStore.dispatch('searchContacts', {
                query: '',
                limit: 50
            });
        }

        this.contextStore.commit('setLoading', true);

        if (!this.isNewOpportunity) {
            Promise.all([
                this.loadOpportunityDetails(),
                this.loadCustomOpportunityFields(),
                this.loadOpportunityFiles(),
                this.loadActivities(),
                this.loadInitialUser(),
            ]).catch((error) => {
                this.closeWidget();
                this.$helpers.showFailNotification(
                    'An error occurred while fetching your opportunities.',
                    { error }
                );
            }).finally(() => {
                this.contextStore.commit('setLoading', false);
            });
        } else {
            Promise.all([
                this.setCustomFields(),
                this.loadInitialUser(),
                this.loadInitialCompany(),
                this.loadInitialContact(),
            ])
            .finally(() => {
                this.contextStore.commit('setLoading', false);
            });
        }
    },

    beforeDestroy() {
        EventBus.$off('opportunity:update', this.handleOpportunityUpdate);
        EventBus.$off('opportunity-widget:validate-and-save', this.handleValidateAndSave);
        EventBus.$off('custom-field:update', this.loadCustomOpportunityFields);
    },

    methods: {
        loadInitialUser() {
            const { userId } = this.widget;
            if(!userId) {
                return Promise.resolve();
            }

            return this.contextStore.dispatch('loadInitialUser', userId);
        },

        loadInitialCompany() {
            const { companyId } = this.widget;
            if (!companyId) {
                return Promise.resolve();
            }

            return this.contextStore.dispatch('loadInitialCompany', companyId);
        },

        loadInitialContact() {
            const { contactId } = this.widget;

            if (!contactId) {
                return Promise.resolve();
            }

            return this.contextStore.dispatch('loadInitialContact', contactId);
        },

        handleChangeNavItem(navItem) {
            const index = navItem.key;
            this.setSelectedNavItemIndex(index);
        },

        handleValidateAndSave() {
            if (this.isNewOpportunity) {
                this.$v.$touch();
                this.contextStore.commit('setSubmitted', true);

                if (this.$v.$invalid) {
                    this.showUnableToSaveOpportunityAlert();
                } else {
                    this.saveOpportunity(false);
                }
            }
        },

        showUnableToSaveOpportunityAlert() {
            const actions = {
                cancel: {
                    label: 'Cancel'
                }
            };

            if (this.selectedNavItemIndex !== 0) {
                actions.confirm = {
                    label: 'Go to Details Tab',
                    action: this.navigateToDetailsTab
                };
            }

            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Unable to Save Opportunity',
                    content:
                        '<p>To save this opportunity, you must complete the required fields on the Details tab.</p>',
                    actions
                });
        },

        handleOpportunityUpdate() {
            this.refreshActivities();
            this.refreshRegisteredDeals();
        },

        loadActivities() {
            return this.contextStore.dispatch('loadActivities');
        },

        refreshActivities() {
            return this.contextStore.dispatch('refreshActivities');
        },

        refreshRegisteredDeals() {
            return this.contextStore.dispatch('refreshRegisteredDeals');
        },

        navigateToDetailsTab() {
            this.setSelectedNavItemIndex(0);
        },

        navigateNextTab() {
            if (this.selectedNavItemIndex === this.navItems.length - 1) {
                return;
            }

            const newIndex = this.selectedNavItemIndex + 1;
            this.setSelectedNavItemIndex(newIndex);
        },

        navigatePreviousTab() {
            if (this.selectedNavItemIndex <= 0) {
                return;
            }

            const newIndex = this.selectedNavItemIndex - 1;
            this.setSelectedNavItemIndex(newIndex);
        },

        saveOpportunity(closeWidget = true) {
            this.contextStore.commit('setSubmitted', true);
            this.$v.$touch();
            if (this.$v.$invalid) {
                if (this.selectedNavItemIndex > 0) {
                    this.showUnableToSaveOpportunityAlert();
                }

                return;
            }

            if (this.isNewOpportunity) {
                this.postOpportunity(closeWidget)
                .then(() => {
                    this.contextStore.commit('setEditing', {
                        editing: !closeWidget,
                        editingContext: !closeWidget ? OPPORTUNITY_EDIT_CONTEXT.All : null
                    });
                });
            } else if (this.isEditingOpportunity) {
                this.patchOpportunity(closeWidget)
                .then(() => {
                    this.contextStore.commit('setEditing', {
                        editing: !closeWidget,
                        editingContext: !closeWidget ? OPPORTUNITY_EDIT_CONTEXT.All : null
                    });
                });
            }
        },

        patchOpportunity(closeWidget = true) {
            return new Promise((resolve, reject) => {
                const closeDate = this.getFormattedCloseDate();
                const discount = this.getDiscountSent();
                const customSolutions = [];
                const distributorSolutions = [];
    
                this.opportunity.itemizedEntry.solutions.forEach((solution) => {
                    if (isNumber(solution.id)) {
                        customSolutions.push(solution);
                    } else {
                        distributorSolutions.push(solution);
                    }
                });
    
                const changes = [
                    {
                        name: 'Name',
                        value: this.opportunity.name
                    },
                    {
                        name: 'ContactId',
                        value: this.opportunity.contact.id
                    },
                    {
                        name: 'CloseDate',
                        value: closeDate
                    },
                    {
                        name: 'DealTypeId',
                        value: this.opportunity.type ? this.opportunity.type.id : null
                    },
                    {
                        name: 'Price',
                        value: parseFloat(
                            (this.isPricingDetailsSimpleEntry
                                ? this.opportunity.simpleEntry.price
                                : this.opportunity.itemizedEntry.price) || 0
                        )
                    },
                    {
                        name: 'Duration',
                        value: parseInt(this.opportunity.simpleEntry.duration || 0)
                    },
                    {
                        name: 'PriceTypeId',
                        value: this.isPricingDetailsSimpleEntry
                            ? this.opportunity.simpleEntry.priceType.id
                            : null
                    },
                    {
                        name: 'DealStatusId',
                        value: this.opportunity.status ? this.opportunity.status.id : null
                    },
                    {
                        name: 'IsWon',
                        value: this.opportunity.status ? this.opportunity.status.isWon : null
                    },
                    {
                        name: 'LineItems',
                        value: this.isPricingDetailsSimpleEntry
                            ? []
                            : this.mapCustomSolutionsToLineItemsRequestBody(customSolutions)
                    },
                    {
                        name: 'DistributorLineItems',
                        value: this.isPricingDetailsSimpleEntry
                            ? []
                            : this.isPricingDetailsSimpleEntry
                                ? []
                                : this.mapDistributorSolutionsToLineItemsRequestBody(
                                    distributorSolutions
                                )
                    },
                    {
                        name: 'Description',
                        value: this.opportunity.description
                    },
                    {
                        name: 'SalesPersonsIds',
                        value: this.opportunity.salespersons.map((salesperson) => salesperson.id)
                    },
                    {
                        name: 'AdditionalContactsIds',
                        value: this.opportunity.secondaryContacts.map((contact) => contact.id)
                    },
                    {
                        name: 'Cost',
                        value: parseFloat(
                            this.isPricingDetailsSimpleEntry
                                ? this.opportunity.simpleEntry.cost
                                : this.opportunity.itemizedEntry.totalCost || 0
                        )
                    },
                    {
                        name: 'GrossMargin',
                        value: this.opportunity.grossMargin.amount
                    },
    
                    {
                        name: 'IsApproved',
                        value: this.hasMetMinimumGrossMargin ? true : null
                    },
                    {
                        name: 'IsBelowThreshold',
                        value: !!this.minimumGrossMargin && !this.hasMetMinimumGrossMargin
                    },
                    {
                        name: 'Discount',
                        value: discount.discountValue
                            ? parseInt(discount.discountValue || '0')
                            : discount.discountValue || 0
                    },
                    {
                        name: 'DiscountTypeId',
                        value: discount.discountTypeId
                    }
                ];
    
                this.contextStore.commit('setSaving', true);
                this.$api
                    .patch(`/api/Deals/${this.opportunity.id}`, changes)
                    .then(this.saveCustomFields)
                    .then(() => {
                        EventBus.$emit('opportunity:update');
                        if (closeWidget) {
                            this.$helpers.showSuccessNotification('Opportunity successfully updated.');
                            this.closeWidget();
                        }

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Opportunity failed to update.', { error });
                        reject(error);
                    })
                    .finally(() => {
                        this.contextStore.commit('setSaving', false);
                    });
            });
        },

        postOpportunity(closeWidget = true) {
            return new Promise((resolve, reject) => {
                this.contextStore.commit('setSaving', true);
    
                this.post()
                    .then(() => this.saveCustomFields())
                    .then(() => {
                        EventBus.$emit('opportunity:update');
                        if (closeWidget) {
                            this.createOpportunitySuccessNotification();
                            this.closeWidget();
                            resolve();
                        }
                    })
                    .catch(() => {
                        reject();
                        this.createOpportunityFailedNotification();
                    })
                    .finally(() => {
                        this.contextStore.commit('setSaving', false);
                    });
            });
        },

        post() {
            const discount = this.getDiscountSent();

            const customSolutions = [];
            const distributorSolutions = [];

            const closeDate = this.getFormattedCloseDate();

            this.opportunity.itemizedEntry.solutions.forEach((solution) => {
                if (isNumber(solution.id)) {
                    customSolutions.push(solution);
                } else {
                    distributorSolutions.push(solution);
                }
            });

            const opportunityDto = {
                name: this.opportunity.name,
                createdByUserId: this.opportunity.salesperson
                    ? this.opportunity.salesperson.id
                    : null,

                clientId: this.clientId,
                contactId: this.opportunity.contact.id,
                dealTypeId: this.opportunity.type ? this.opportunity.type.id : null,
                priceTypeId: this.isPricingDetailsSimpleEntry
                    ? this.opportunity.simpleEntry.priceType.id
                    : null,

                price: parseFloat(
                    (this.isPricingDetailsSimpleEntry
                        ? this.opportunity.simpleEntry.price
                        : this.opportunity.itemizedEntry.price) || 0
                ),

                duration: parseInt(this.opportunity.simpleEntry.duration || 0),
                description: this.opportunity.description,
                lineItems: this.isPricingDetailsSimpleEntry
                    ? []
                    : this.mapCustomSolutionsToLineItemsRequestBody(customSolutions),

                distributorLineItems: this.isPricingDetailsSimpleEntry
                    ? []
                    : this.mapDistributorSolutionsToLineItemsRequestBody(distributorSolutions),

                dealStatusId: this.opportunity.status ? this.opportunity.status.id : null,
                isWon: this.opportunity.status ? this.opportunity.status.isWon : null,
                closeDate: closeDate,
                closedByUserId:
                    this.opportunity.status &&
                    this.opportunity.status.isWon &&
                    this.opportunity.salesperson
                        ? this.opportunity.salesperson.id
                        : null,

                additionalContactsIds: this.opportunity.secondaryContacts.map((contact) => contact.id),
                salesPersonsIds: this.opportunity.salespersons.map((salesperson) => salesperson.id),
                cost: parseFloat(
                    this.isPricingDetailsSimpleEntry
                        ? this.opportunity.simpleEntry.totalCost
                        : this.opportunity.itemizedEntry.totalCost || 0
                ),

                grossMargin: this.opportunity.grossMargin.amount,
                isApproved: this.hasMetMinimumGrossMargin ? true : null,
                isBelowThreshold: !!this.minimumGrossMargin && !this.hasMetMinimumGrossMargin,
                discount: discount.discountValue
                    ? parseInt(discount.discountValue || '0')
                    : discount.discountValue || 0,

                discountTypeId: discount.discountTypeId
            };

            return new Promise((resolve, reject) => {
                this.$api
                    .post('/api/Deals/', opportunityDto)
                    .then((response) => {
                        this.updateOpportunity((o) => {
                            o.id = response.data.Id;
                        });

                        this.contextStore.dispatch('uploadFiles');
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                    });
            });
        },

        getDiscountSent() {
            let discountSent;

            if (this.isPricingDetailsSimpleEntry) {
                const {
                    simpleEntry: { discount, discountType }
                } = this.opportunity;

                discountSent = {
                    discountValue: discount,
                    discountTypeId: this.discountOptions.find(({ value }) => value === discountType)
                        .id
                };
            } else {
                const {
                    itemizedEntry: {
                        hasDiscount,
                        discountType,
                        totalDiscountValue,
                        totalDiscountType,
                        itemDiscountValue,
                        itemDiscountType
                    }
                } = this.opportunity;

                if (!hasDiscount) {
                    discountSent = {
                        discountValue: null,
                        discountTypeId: 1
                    };
                } else {
                    if (discountType === 'total') {
                        discountSent = {
                            discountValue: totalDiscountValue,
                            discountTypeId: this.discountOptions.find(
                                ({ value }) => value === totalDiscountType
                            ).id
                        };
                    } else {
                        discountSent = {
                            discountValue: itemDiscountValue,
                            discountTypeId: this.discountOptions.find(
                                ({ value }) => value === itemDiscountType
                            ).id
                        };
                    }
                }
            }
            return discountSent;
        },

        getFormattedCloseDate() {
            const isClosed = this.opportunity.status && this.opportunity.status.isWon !== null;

            if (isClosed) {
                const closeDate = this.opportunity.closeDate;

                if (closeDate) {
                    const momentDate = this.$moment(closeDate);
                    return momentDate.utc().format('YYYY-MM-DDTHH:mm');
                } else {
                    return null;
                }
            }

            return null;
        },

        mapCustomSolutionsToLineItemsRequestBody(solutions) {
            const getUnitDiscountAmount = (item) => {
                const { discount, price } = item;
                if (this.opportunity.itemizedEntry.itemDiscountType === 'percentage') {
                    return (discount * price) / 100;
                } else {
                    return discount;
                }
            };

            const {
                itemizedEntry: { discountType, hasDiscount }
            } = this.opportunity;

            return solutions.map((item) => ({
                Id: item.id,
                ConnectWiseDealLineItemId: item.connectWiseDealLineItemId,
                TermInMonths:
                    this.$helpers.getTermInMonths({ name: item.billingCycle }) * item.term,
                UnitDiscount: discountType === 'total' || !hasDiscount ? null : item.discount,
                Quantity: item.qty,
                SolutionId: item.solutionId,
                UnitPrice: item.price,
                UnitCost: item.cost,
                BillingCycle: this.getSolutionBillingCyclesValue(item.billingCycle),
                TotalMargin: this.$helpers.calculateMargin({
                    ...item,
                    unitDiscountAmount: getUnitDiscountAmount(item),
                    termInMonths:
                        this.$helpers.getTermInMonths({ name: item.billingCycle }) * item.term
                })
            }));
        },

        mapDistributorSolutionsToLineItemsRequestBody(solutions) {
            const getUnitDiscountAmount = (item) => {
                const { discount, price } = item;
                if (this.opportunity.itemizedEntry.itemDiscountType === 'percentage') {
                    return (discount * price) / 100;
                } else {
                    return discount;
                }
            };

            const {
                itemizedEntry: { discountType, hasDiscount }
            } = this.opportunity;
            return solutions.map((item) => ({
                Quantity: item.qty,
                BillingCycle: this.getSolutionBillingCyclesValue(item.billingCycle),
                TermInMonths: item.term
                    ? this.$helpers.getTermInMonths({ name: item.billingCycle }) * item.term
                    : 1,
                TotalMargin: this.$helpers.calculateMargin({
                    ...item,
                    unitDiscountAmount: getUnitDiscountAmount(item),
                    termInMonths: item.term
                        ? this.$helpers.getTermInMonths({ name: item.billingCycle }) * item.term
                        : 1
                }),
                UnitCost: item.cost,
                UnitDiscount: discountType === 'total' || !hasDiscount ? 0 : item.discount,
                UnitPrice: item.price,
                UnitMargin: 0,

                Description: item.description,
                Name: item.name,
                Url: item.previewImageUrl,
                FileMimeType: '',
                VideoUrl: '',
                VideoThumbnailUrl: ''
            }));
        },

        getSolutionBillingCyclesValue(billingCycleName) {
            const billingCycles = this.$helpers.getSolutionBillingCycles();

            const billingCycleValue = billingCycles.find(
                (cycle) => cycle.name === billingCycleName
            );
            return billingCycleValue.value;
        },

        createOpportunitySuccessNotification() {
            const contactUrl = `/Contacts/${this.opportunity.contact.id}`;

            const notification = {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: 'Opportunity successfully created!',
                actionLabel: 'See contact',
                action: () => {
                    this.$router.push(contactUrl);
                    EventBus.$emit('close-notification', this.notification);
                }
            };

            EventBus.$emit('show-notification', notification);
        },

        createOpportunityFailedNotification() {
            const notification = {
                iconUrl: this.opportunity.contact.imageUrl || require('@/assets/images/avatar.png'),
                subIcon: 'warning',
                message: 'Opportunity creation failed.',
                actionLabel: null,
                action: () => ({})
            };

            EventBus.$emit('show-notification', notification);
        },

        saveCustomFields() {
            const service = '/ms/custom-fields/v1/customOpportunityFieldValues';
            return new Promise((resolve, reject) => {
                const changedOpportunityCustomFields = this.getModifiedOrNewObjects(
                    this.initialCustomOpportunityFields,
                    this.customOpportunityFields
                );

                const valueRequests = changedOpportunityCustomFields.map((item) => { 
                    const customFieldValue = {
                        OpportunityAccountId: this.customFieldsConfig.opportunityId,
                        CustomOpportunityFieldId: parseInt(item.id),
                        [item.customFieldValue.name]: this.customFieldValue(
                            item.customFieldValue.value,
                            item.inputType
                        )
                    };

                    if (item.customFieldValueId) {
                        return this.$api.put(
                            `${service}/${item.customFieldValueId}/${this.customFieldsConfig.clientId}`,
                            customFieldValue
                        );
                    }

                    return this.$api.post(
                        `${service}/${this.customFieldsConfig.clientId}`,
                        customFieldValue
                    );
                });

                Promise.all(valueRequests)
                    .then(resolve)
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'Failed to save your opportunity custom fields.',
                            { error }
                        );
                        reject(error);
                    });
            });
        },

        customFieldValue(value, type) {
            if (type.includes('checkbox') || type === 'single dropdown') {
                return JSON.stringify(value);
            } else if (type === 'number') {
                return +value;
            }
            return value;
        },

        getModifiedOrNewObjects(oldArray, newArray) {
            return differenceWith(newArray, oldArray, isEqual);
        },

        editOpportunity() {
            this.contextStore.commit('setEditing', {
                editing: true,
                editingContext: OPPORTUNITY_EDIT_CONTEXT.Details
            });
        },

        setSelectedNavItemIndex(navItemIndex) {
            this.contextStore.commit('setSelectedNavItemIndex', navItemIndex);
        },

        loadOpportunityDetails() {
            return this.contextStore.dispatch('loadOpportunityDetails');
        },

        loadCustomOpportunityFields() {
            this.contextStore.commit('setLoadingCustomFields', true);
            return new Promise((resolve) => {
                this.getCustomFields({}, this.customFieldsConfig)
                    .then(({ items }) => this.loadCustomFieldValues(items))
                    .then((values) => this.contextStore.commit('setCustomFields', values))
                    .then(resolve)
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your custom fields data.',
                            { error }
                        );
                    })
                    .finally(() => {
                        this.contextStore.commit('setLoadingCustomFields', false);
                    });
            });
        },

        setCustomFields() {
            this.getCustomFields({}, this.customFieldsConfig)
                .then(({ items }) =>  {
                    this.setEmptyCustomFieldValues(items);
                    this.contextStore.commit('setCustomFields', this.customOpportunityFields);
                })
        },

        loadCustomFieldValues(items) {
            return new Promise((resolve, reject) => {
                const valuesRequests = items.map((item) => {
                    return this.getCustomFieldValue(this.customFieldsConfig, item, !this.isEditingOpportunity);
                });

                Promise.all(valuesRequests)
                    .then((values) => {
                        this.customOpportunityFields = values;
                        this.initialCustomOpportunityFields = cloneDeep(
                            this.customOpportunityFields
                        );

                        resolve(values);
                    })
                    .catch(reject);
            });
        },

        setEmptyCustomFieldValues(items) {
            items.forEach((item) => {
                var customFieldValue = {
                name: null,
                value: null
            };

            var generalInputType;
                switch (item.inputType) {
                case 'multi-line text':
                    generalInputType = 'textarea';
                    customFieldValue.name = 'LongStringValue';
                    break;
                case 'number':
                    generalInputType = 'number';
                    customFieldValue.name = 'DoubleValue';
                    break;
                case 'date picker':
                    generalInputType = 'date-picker';
                    customFieldValue.name = 'DateTimeValue';
                    break;
                case 'single checkbox':
                case 'multiple checkboxes':
                    generalInputType = 'checkbox';
                    customFieldValue.name = 'ObjectValueJson';
                    break;
                case 'single dropdown':
                    generalInputType = 'dropdown';
                    customFieldValue.name = 'ObjectValueJson';
                    break;
                default:
                    generalInputType = 'input';
                    customFieldValue.name = 'ShortStringValue';
            }

            this.customOpportunityFields.push({
                customFieldValue: customFieldValue,
                generalInputType: generalInputType,
                inputType: item.inputType,
                id: item.id,
                name: item.name,
                description: item.description
            });
        });

        this.initialCustomOpportunityFields = cloneDeep(
                this.customOpportunityFields
            );
        },

        loadOpportunityFiles() {
            return this.contextStore.dispatch('loadFiles');
        },

        handleClickCloseWidget() {
            if (!this.isNewOpportunity && this.isEditingOpportunity) {
                this.$modal.show(
                    AppPopup,
                    {
                        type: 'alert-popup',
                        title: 'Changes will be lost',
                        content:
                            '<p>If you leave now, all changes to this opportunity will be lost. Are you sure you want to proceed?</p>',
                        actions: {
                            confirm: {
                                label: 'Yes',
                                action: this.closeWidget
                            },

                            cancel: {
                                label: 'Go Back'
                            }
                        }
                    });
            } else {
                this.closeWidget();
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        }
    }

};
</script>

<style>
.opportunity-details-widget .content-wrapper {
    display: flex;
    justify-content: center;
}

.opportunity-details-widget .content-wrapper .loading-screen {
    margin-left: 0;
}

.opportunity-details-widget .optional {
    font: normal normal normal 12px/24px Roboto;
    margin-top: 5px;
}

.opportunity-details-widget .tab-navigation li {
    min-width: fit-content;
    flex: 1;
}

.opportunity-details-widget .tab-navigation ul {
    background: #ffffff;
    position: fixed;
    z-index: 1;
    padding-top: 24px;
    top: 67px;
    width: 100%;
    max-width: 630px;
    justify-content: space-between;
}

.opportunity-details-widget .widget-container {
    width: 100%;
    height: 100%;
}

.opportunity-details-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    color: #382153;
}

.opportunity-details-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.opportunity-details-widget .app-button {
    display: flex;
    align-items: center;
    gap: 8px;
}


.opportunity-details-widget .opportunity-solution-element svg path {
    fill: #382153;
}

.opportunity-details-widget .btn-secondary {
    border: 1px solid #18aeff;
    background: transparent;
    color: #18aeff;

    &:disabled {
        border-color: #CAC6CB;

        svg path {
            fill: #CAC6CB;
        }
    }
}

.opportunity-details-widget .btn-secondary:hover {
    background: #dbedfa;
}


.opportunity-details-widget .btn-secondary svg path {
    fill: #18aeff;
}

.opportunity-details-widget .activity-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
}

.opportunity-details-widget .activity-buttons .previously-logged-btn {
    border: 1px solid #18aeff;
    background-color: transparent;
    color: #18aeff;
}

.opportunity-details-widget .activity-buttons .previously-logged-btn:hover {
    background: #dbedfa;
}

.opportunity-details-widget .activity-buttons .previously-logged-btn .o6-add-svg path {
    stroke: #18aeff;
    fill: #18aeff;
}

.opportunity-details-widget .action-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;
}

.opportunity-details-widget .action-container svg {
    margin-left: 8px;
}

.opportunity-details-widget .opportunity-details-tabs {
    margin-top: 64px;
    width: 100%;
}

.opportunity-details-widget.navigation-disabled .opportunity-details-tabs {
    margin-top: 0;
}

.opportunity-details-widget .opportunity-details-tabs > * {
    padding-bottom: 2rem;
}

.opportunity-details-widget .opportunity-custom-fields {
    margin-bottom: 0;
}


.opportunity-details-widget .error-message {
    display: block;
    margin: 0.8em 0 1.25em 0;
    font-size: 12px;
    color: #f30070;
}

.opportunity-details-widget .tab-header-optional {
    color: #877D88;
}

.opportunity-details-widget .divider {
    height: 1px;
    background-color: #cac6cb;
    margin: 24px 4px;
}

.opportunity-details-widget .outlined-section {
    position: relative;
    border: 1px solid #cac6cb;
    border-radius: 16px;
    padding: 32px 24px 8px;
}

.opportunity-details-widget .outlined-section:not(:last-child) {
    margin-bottom: 40px;
}

.opportunity-details-widget .outlined-section .section-title {
    font: normal normal bold 18px/24px Roboto;
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
    margin: auto;
    width: fit-content;
    padding: 0px 25px;
    background-color: #ffffff;
    font-weight: 600;
}

.opportunity-details-widget .outlined-section .content-container {
    font-size: 16px;
}

.opportunity-details-widget .field-container.error {
    .multiselect__tags,
    .v-input__control fieldset,
    .v-input__control:hover fieldset,
    .app-textarea textarea {
        border-color: #f30070 !important;
    }
}

.opportunity-details-widget .app-select .v-text-field__details {
    display: none;
}

.opportunity-details-widget .app-select .v-input__slot {
    margin: 0;
}
</style>